<script setup>
import { ref, shallowRef } from 'vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';

useSessionGuard();

const headers = [{ title: 'User type', key: 'description' }];
const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', '/user-type/getAllUsertype'),
);
load();

const fields = [{ key: 'description', label: 'Description', required: true, placeholder: 'e.g. Faculty' }];
const editing = shallowRef(null); // null = add
const formOpen = shallowRef(false);
const form = ref({ description: '' });
const { busy, run } = useAsyncAction();

function openForm(type = null) {
  editing.value = type;
  form.value = { description: type?.description ?? '' };
  formOpen.value = true;
}

async function save() {
  const data = { description: form.value.description.trim() };
  const ok = await run(
    () =>
      editing.value
        ? apiRequest('patch', `/user-type/${editing.value.id}`, data)
        : apiRequest('post', '/user-type', data),
    {
      success: editing.value ? 'User type updated.' : 'User type added.',
      error: 'Could not save the user type.',
    },
  );
  if (ok) {
    formOpen.value = false;
    load();
  }
}
</script>

<template>
  <UiTableCard
    title="User types"
    description="The kinds of accounts users can belong to."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    add-label="Add user type"
    empty-text="No user types yet."
    row-label="description"
    :row-actions="(type) => [{ title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(type) }]"
    @add="openForm()"
  >
    <template #[`item.description`]="{ item }">
      <span class="font-weight-medium">{{ item.description }}</span>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="editing ? 'Edit user type' : 'Add user type'"
    :fields="fields"
    v-model:values="form"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add user type'"
    @submit="save"
  />
</template>
